import { useEffect, useState, useMemo, Fragment } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { formatEUR, formatEURDecimal, SPANISH_MONTHS_SHORT } from "../lib/format";
import { toast } from "sonner";
import { ArrowLeft, Download, Loader2, Banknote } from "lucide-react";

const QUINCENAS = [];
for (let m = 1; m <= 12; m++) {
  for (let q = 1; q <= 2; q++) QUINCENAS.push({ key: `${m}-${q}`, month: m, quincena: q });
}

export default function ArchivoView() {
  const { id } = useParams();
  const [arch, setArch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(0.85);
  const [showPastMonths, setShowPastMonths] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/weekly-archives/${id}/cashflow`)
      .then(r => setArch(r.data))
      .catch(e => setError(e?.response?.data?.detail || "Error cargando archivo"))
      .finally(() => setLoading(false));
  }, [id]);

  const downloadXlsx = async () => {
    try {
      const r = await api.get(`/weekly-archives/${id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = arch?.filename || `archivo_${id}.xlsx`;
      document.body.appendChild(a);
      a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Error descargando"); }
  };

  const data = arch?.cashflow;
  const banks = arch?.banks || [];
  const projects = data?.projects || [];

  const visibleQuincenas = useMemo(() => {
    if (showPastMonths) return QUINCENAS;
    if (!data?.current_quincena) return QUINCENAS;
    const cur = data.current_quincena;
    return QUINCENAS.filter(q => q.month > cur.month || (q.month === cur.month && q.quincena >= cur.quincena));
  }, [showPastMonths, data]);

  if (loading) {
    return <div className="p-8 text-center text-sm text-[var(--ink-muted)]"><Loader2 className="inline animate-spin mr-2" size={14}/>Cargando archivo…</div>;
  }
  if (error) {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <Link to="/archivos" className="inline-flex items-center gap-1 text-xs text-[var(--ink-muted)] hover:text-[var(--ink)] mb-3"><ArrowLeft size={12}/> Volver a archivos</Link>
        <div className="bg-amber-50 border border-amber-300 text-amber-900 p-4 rounded text-sm" data-testid="archivo-view-error">
          {error}
          <div className="mt-3">
            <button onClick={downloadXlsx} className="inline-flex items-center gap-2 border bg-white px-3 py-2 rounded text-xs hover:bg-[var(--hover)]"><Download size={13}/> Descargar Excel</button>
          </div>
        </div>
      </div>
    );
  }
  if (!data) return null;

  const totalBanks = banks.reduce((s, b) => s + (b.balance || 0), 0);

  return (
    <div className="p-6 max-w-full" data-testid="archivo-view-page" style={{ fontSize: `${zoom}rem` }}>
      <Link to="/archivos" className="inline-flex items-center gap-1 text-xs text-[var(--ink-muted)] hover:text-[var(--ink)] mb-3"><ArrowLeft size={12}/> Volver a archivos</Link>

      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <p className="font-mono-num text-[10px] uppercase tracking-widest text-[var(--ink-muted)]">Archivo semanal (solo lectura)</p>
          <h1 className="font-display text-3xl font-black tracking-tight" data-testid="archivo-view-title">{arch.filename}</h1>
          <p className="text-xs text-[var(--ink-muted)] mt-1">
            Año datos <span className="font-mono-num font-semibold text-[var(--ink)]">{arch.year}</span>
            {arch.quincena_q && arch.quincena_month ? <> · cerrado en la <span className="font-mono-num">{arch.quincena_q}ª quincena de {SPANISH_MONTHS_SHORT[(arch.quincena_month||1)-1]}</span></> : null}
            {arch.created_at ? <> · {new Date(arch.created_at).toLocaleString("es-ES", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</> : null}
            {arch.created_by ? <> · por {arch.created_by}</> : null}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="flex items-center gap-1 text-xs">
            <input type="checkbox" checked={showPastMonths} onChange={e=>setShowPastMonths(e.target.checked)} data-testid="archivo-toggle-past"/>
            <span>Mostrar meses anteriores</span>
          </label>
          <div className="flex items-center border border-[var(--border)] bg-white">
            <button onClick={() => setZoom(z => Math.max(0.55, z - 0.1))} className="px-2 py-1 hover:bg-[var(--hover)] text-xs" title="Reducir">−</button>
            <span className="font-mono-num text-[10px] uppercase tracking-widest text-[var(--ink-muted)] px-2 min-w-[42px] text-center">{Math.round(zoom*100)}%</span>
            <button onClick={() => setZoom(z => Math.min(1.4, z + 0.1))} className="px-2 py-1 hover:bg-[var(--hover)] text-xs" title="Ampliar">+</button>
          </div>
          <button
            data-testid="archivo-view-download"
            onClick={downloadXlsx}
            className="border border-[var(--border)] bg-white text-sm px-3 py-2 inline-flex items-center gap-2 hover:bg-[var(--hover)]"
            title="Descargar el Excel completo del archivo"
          >
            <Download size={13}/> Excel
          </button>
        </div>
      </div>

      {/* Banks snapshot */}
      {banks.length > 0 && (
        <div className="bg-white border border-[var(--border)] mb-5 p-4" data-testid="archivo-banks-snapshot">
          <div className="flex items-center gap-2 mb-3">
            <Banknote size={14}/>
            <h3 className="font-display font-black text-base">Bancos al cierre</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {banks.map((b, i) => (
              <div key={i} className="border border-[var(--border)] px-3 py-2">
                <div className="text-[10px] uppercase tracking-widest text-[var(--ink-muted)] font-mono-num truncate" title={b.name}>{b.name}</div>
                <div className="font-mono-num text-sm font-bold">{formatEURDecimal(b.balance || 0)}</div>
              </div>
            ))}
            <div className="bg-black text-white px-3 py-2">
              <div className="text-[10px] uppercase tracking-widest text-white/70 font-mono-num">Total</div>
              <div className="font-mono-num text-sm font-bold">{formatEURDecimal(totalBanks)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Cashflow grid (read-only, misma estructura que Cashflow.jsx) */}
      <div className="border border-[var(--border)] bg-white overflow-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-black text-white">
              <th className="sticky-col bg-black text-white text-left px-3 py-2 text-[10px] uppercase tracking-widest font-mono-num">Proyecto</th>
              {visibleQuincenas.map(q => (
                <th key={q.key} className="px-2 py-2 text-right text-[10px] uppercase tracking-widest font-mono-num">
                  {q.quincena}ª {SPANISH_MONTHS_SHORT[q.month-1]}
                </th>
              ))}
              <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest font-mono-num">Total</th>
            </tr>
          </thead>
          <tbody>
            {projects.map(p => {
              const totalIngreso = p.total_ingreso + (p.total_ingreso_previsto || 0);
              const totalGasto = p.total_gasto + (p.total_gasto_previsto || 0);
              const projectNet = totalIngreso - totalGasto;
              return (
                <Fragment key={p.project_code}>
                  <tr className="bg-[var(--hover)] border-t-2 border-[var(--ink)]" data-testid={`archivo-project-row-${p.project_code}`}>
                    <td className="sticky-col bg-[var(--hover)] px-3 py-2">
                      <div className="min-w-0">
                        <div className="font-mono-num text-[10px] uppercase tracking-wider text-[var(--ink-muted)]">{p.project_code}</div>
                        <div className="text-xs font-bold truncate max-w-[260px]" title={p.project_name}>{p.project_name || "—"}</div>
                      </div>
                    </td>
                    {visibleQuincenas.map(q => <td key={q.key} className="px-2 py-2"></td>)}
                    <td className={`px-3 py-2 text-right font-mono-num font-bold ${projectNet >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                      {projectNet >= 0 ? "+" : "−"}{formatEUR(Math.abs(projectNet))}
                    </td>
                  </tr>
                  <tr className="bg-white">
                    <td className="sticky-col bg-white px-3 py-1 pl-6 text-[10px] uppercase tracking-widest font-mono-num text-emerald-800">Ingreso</td>
                    {visibleQuincenas.map(q => {
                      const cell = p.cells[q.key];
                      const real = cell?.Ingreso || 0;
                      const prev = cell?.Ingreso_previsto || 0;
                      return (
                        <td key={q.key} className="px-2 py-1 text-right font-mono-num">
                          {real ? <div className="text-emerald-700">{formatEUR(real)}</div> : null}
                          {prev ? <div className="text-emerald-700/60 italic text-[10px]" title="Previsto">~{formatEUR(prev)}</div> : null}
                        </td>
                      );
                    })}
                    <td className="px-3 py-1 text-right font-mono-num font-bold text-emerald-700">
                      {p.total_ingreso ? formatEUR(p.total_ingreso) : ""}
                      {p.total_ingreso_previsto ? <div className="text-emerald-700/60 italic text-[10px] font-normal">~{formatEUR(p.total_ingreso_previsto)}</div> : null}
                    </td>
                  </tr>
                  <tr className="bg-white">
                    <td className="sticky-col bg-white px-3 py-1 pl-6 text-[10px] uppercase tracking-widest font-mono-num text-red-800">Gasto</td>
                    {visibleQuincenas.map(q => {
                      const cell = p.cells[q.key];
                      const real = cell?.Gasto || 0;
                      const prev = cell?.Gasto_previsto || 0;
                      return (
                        <td key={q.key} className="px-2 py-1 text-right font-mono-num">
                          {real ? <div className="text-red-700">{formatEUR(real)}</div> : null}
                          {prev ? <div className="text-red-700/60 italic text-[10px]" title="Previsto">~{formatEUR(prev)}</div> : null}
                        </td>
                      );
                    })}
                    <td className="px-3 py-1 text-right font-mono-num font-bold text-red-700">
                      {p.total_gasto ? formatEUR(p.total_gasto) : ""}
                      {p.total_gasto_previsto ? <div className="text-red-700/60 italic text-[10px] font-normal">~{formatEUR(p.total_gasto_previsto)}</div> : null}
                    </td>
                  </tr>
                </Fragment>
              );
            })}
            {data.fixed_expenses && data.fixed_expenses.length > 0 && (
              <Fragment>
                <tr className="bg-zinc-800 text-white">
                  <td className="sticky-col bg-zinc-800 text-white px-3 py-2 text-[10px] uppercase tracking-widest font-mono-num font-bold" colSpan={visibleQuincenas.length + 2}>
                    Estructura / Impuestos
                  </td>
                </tr>
                {data.fixed_expenses.map((fe, i) => (
                  <tr key={i} className="bg-white">
                    <td className="sticky-col bg-white px-3 py-2">
                      <div className="text-xs font-bold tracking-wide">{fe.name}</div>
                    </td>
                    {visibleQuincenas.map(q => {
                      const v = fe.cells[q.key] || 0;
                      return <td key={q.key} className="px-2 py-2 text-right font-mono-num text-red-700">{v ? `−${formatEUR(v)}` : ""}</td>;
                    })}
                    <td className="px-3 py-2 text-right font-mono-num font-bold text-red-700">
                      {fe.total ? `−${formatEUR(fe.total)}` : ""}
                    </td>
                  </tr>
                ))}
              </Fragment>
            )}
            <tr className="bg-emerald-50 border-t-2 border-[var(--ink)]" data-testid="archivo-row-total-ingresos">
              <td className="sticky-col bg-emerald-50 px-3 py-2 text-[10px] uppercase tracking-widest font-mono-num font-bold text-emerald-800">Total Ingresos</td>
              {visibleQuincenas.map(q => {
                const t = data.column_totals[q.key];
                const v = (t?.Ingreso || 0) + (t?.Ingreso_previsto || 0);
                return (
                  <td key={q.key} className="px-2 py-2 text-right font-mono-num font-bold text-emerald-700">
                    {v === 0 ? "" : "+" + formatEUR(v)}
                  </td>
                );
              })}
              <td className="px-3 py-2"></td>
            </tr>
            <tr className="bg-red-50" data-testid="archivo-row-total-gastos">
              <td className="sticky-col bg-red-50 px-3 py-2 text-[10px] uppercase tracking-widest font-mono-num font-bold text-red-800">Total Gastos</td>
              {visibleQuincenas.map(q => {
                const t = data.column_totals[q.key];
                const v = (t?.Gasto || 0) + (t?.Gasto_previsto || 0) + (t?.GastoFijo || 0);
                return (
                  <td key={q.key} className="px-2 py-2 text-right font-mono-num font-bold text-red-700">
                    {v === 0 ? "" : "−" + formatEUR(v)}
                  </td>
                );
              })}
              <td className="px-3 py-2"></td>
            </tr>
            <tr className="bg-zinc-100 border-t border-[var(--ink)]" data-testid="archivo-row-balance">
              <td className="sticky-col bg-zinc-100 px-3 py-2 text-[10px] uppercase tracking-widest font-mono-num font-bold">Balance</td>
              {visibleQuincenas.map(q => {
                const v = data.column_totals[q.key]?.Neto || 0;
                return (
                  <td key={q.key} className={`px-2 py-2 text-right font-mono-num font-bold ${v >= 0 ? "text-emerald-700" : v < 0 ? "text-red-700" : ""}`}>
                    {v === 0 ? "" : (v >= 0 ? "+" : "−") + formatEUR(Math.abs(v))}
                  </td>
                );
              })}
              <td className="px-3 py-2"></td>
            </tr>
            <tr className="bg-black text-white sticky bottom-0" data-testid="archivo-row-cashflow">
              <td className="sticky-col bg-black text-white px-3 py-2 text-[10px] uppercase tracking-widest font-mono-num font-black">Cash flow</td>
              {data.cumulative.filter(c => visibleQuincenas.some(q => q.key === c.key)).map(c => (
                <td key={c.key} className={`px-2 py-2 text-right font-mono-num font-bold ${c.value >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                  {formatEUR(c.value)}
                </td>
              ))}
              <td className="px-3 py-2"></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
